import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { resetFavorites } from "../hooks/useFavorites";
import { apiFetch } from "../utils/api";

interface Props {
  backTo?: string;    // 뒤로가기 경로 ("_back" = history.back())
  backLabel?: string; // 뒤로가기 텍스트
}

export function AppHeader({ backTo, backLabel = "← 이전" }: Props) {
  const navigate = useNavigate();
  const m = useIsMobile();
  const [unreadCount, setUnreadCount] = useState(0);

  const brokerInfo = (() => {
    try { return JSON.parse(localStorage.getItem("broker_info") || "null"); }
    catch { return null; }
  })();
  const isLoggedIn = !!localStorage.getItem("broker_token");
  const isAdmin = brokerInfo?.role === "admin";

  // 미읽음 문의 수 조회
  useEffect(() => {
    if (!isLoggedIn) return;
    apiFetch<{ unread_inquiries: number }>("/api/dashboard/stats")
      .then((d) => setUnreadCount(d.unread_inquiries || 0))
      .catch((e) => console.warn('dashboard stats fetch failed:', e));
  }, [isLoggedIn]);

  function handleLogout() {
    localStorage.removeItem("broker_token");
    localStorage.removeItem("broker_info");
    resetFavorites();
    navigate("/login");
  }

  const st = ahStyles(m);

  return (
    <header style={st.header}>
      <div style={st.inner}>
        {/* 로고 + 뒤로가기 */}
        <div style={st.left}>
          {backTo && (
            <button
              onClick={() => backTo === "_back" ? navigate(-1) : navigate(backTo)}
              style={st.backBtn}
            >
              {backLabel}
            </button>
          )}
          <Link to="/" style={st.logo}>
            <span style={{
              display: "block", fontSize: m ? 12 : 13, fontWeight: 600,
              color: "var(--lt-gold)", letterSpacing: "0.1em",
              lineHeight: 1, marginBottom: 2,
            }}>다가치</span>
            <span style={{
              display: "block", fontSize: m ? 8 : 9, fontWeight: 400,
              color: "rgba(212,168,83,0.7)", letterSpacing: "0.04em",
              lineHeight: 1, marginBottom: 4,
            }}>다같이 · 다양한 가치</span>
            <span style={{
              display: "block", fontSize: m ? 20 : 24, fontWeight: 800,
              color: "var(--text-primary)", letterSpacing: "-0.5px", lineHeight: 1,
            }}>
              <span style={{ color: "var(--lt-gold)" }}>땅</span>신뢰
            </span>
          </Link>
        </div>

        {/* 네비게이션 */}
        <nav style={st.nav}>
          <Link to="/listings" style={st.navLink}>매물 보기</Link>
          {!m && <Link to="/burial" style={st.navLink}>{"\u26B0\uFE0F"} 묘터</Link>}
          {isLoggedIn ? (
            <>
              {!m && brokerInfo?.is_verified && <Link to="/owner-listings" style={st.navLink}>{"\uD83C\uDF31"} 땅주인</Link>}
              {!m && <Link to={`/agent/${brokerInfo?.id}`} style={st.navLink}>내 프로필</Link>}
              {!m && <Link to="/listings/new" style={st.navLink}>매물 등록</Link>}
              <Link to="/mypage" style={{ ...st.navLink, position: "relative" as const, display: "inline-flex", alignItems: "center", gap: 4 }}>
                {m ? "\uB9C8\uC774" : "\uB9C8\uC774\uD398\uC774\uC9C0"}
                {unreadCount > 0 && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 16, height: 16, borderRadius: 8,
                    background: "#EF4444", color: "#fff",
                    fontSize: 10, fontWeight: 700, padding: "0 4px",
                  }}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>
              {isAdmin && !m && <Link to="/admin" style={st.navLink}>관리자</Link>}
              <button onClick={handleLogout} style={st.logoutBtn}>로그아웃</button>
            </>
          ) : (
            <>
              <Link to="/login" style={st.navLink}>로그인</Link>
              {!m && <Link to="/signup" style={st.navBtn}>회원가입</Link>}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function ahStyles(m: boolean): Record<string, React.CSSProperties> {
  return {
    header: {
      background: "var(--bg-card)",
      borderBottom: "1px solid var(--border-light)",
      boxShadow: "0 1px 0 var(--lt-gold-border, rgba(212,168,83,0.15))",
      position: "sticky" as const,
      top: 0,
      zIndex: 100,
    },
    inner: {
      maxWidth: 1100,
      margin: "0 auto",
      padding: m ? "0 12px" : "0 20px",
      height: m ? 56 : 64,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    left: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    logo: {
      display: "inline-flex",
      flexDirection: "column" as const,
      textDecoration: "none",
      lineHeight: 1,
    },
    backBtn: {
      background: "none",
      border: "none",
      fontSize: m ? 13 : 14,
      color: "var(--text-muted)",
      cursor: "pointer",
      padding: 0,
      fontWeight: 500,
    },
    nav: {
      display: "flex",
      alignItems: "center",
      gap: m ? 8 : 16,
    },
    navLink: {
      fontSize: m ? 13 : 14,
      color: "var(--text-secondary)",
      textDecoration: "none",
      fontWeight: 500,
    },
    navBtn: {
      fontSize: 13,
      color: "#0F172A",
      background: "var(--lt-gold)",
      padding: "6px 14px",
      borderRadius: 20,
      textDecoration: "none",
      fontWeight: 600,
    },
    logoutBtn: {
      fontSize: m ? 12 : 13,
      color: "var(--text-muted)",
      background: "none",
      border: "1px solid var(--border-light)",
      padding: m ? "4px 8px" : "5px 12px",
      borderRadius: 8,
      cursor: "pointer",
    },
  };
}
